import {
	decodeExclusionReasonFilter,
	encodeExclusionReasonFilter,
	ExclusionReasonFilter,
} from "@signalist/shared-types";

import { InclusionFilterStatus } from "@/enum/list.enum";

import { canonicalReasonParam, reasonParamsOf } from "../../../utils/exclusionReasonMeta";

/**
 * Pure selection logic for the grouped "Show" filter (PREC-3536 Phase 5).
 *
 * Wire contract (mirrors the backend query builder): `statuses` holds every bucket with any
 * selection; `exclusionReasons` holds encoded "STATUS:filterParam" entries ONLY for partially
 * selected buckets — a fully selected bucket sends no reasons and matches wholly (including
 * rows without any exclusion_reason). At least one bucket stays selected, matching the
 * previous dropdown's behavior.
 */
export interface GroupedShowSelection {
	statuses: InclusionFilterStatus[];
	exclusionReasons: string[];
}

/** The children selected in a bucket, each once, by the param the Show filter lists it under. */
const reasonsOf = (selection: GroupedShowSelection, status: InclusionFilterStatus): string[] => [
	...new Set(
		selection.exclusionReasons
			.map(decodeExclusionReasonFilter)
			.filter((decoded): decoded is ExclusionReasonFilter => decoded?.inclusionStatus === status)
			.map((decoded) => canonicalReasonParam(decoded.filterParam))
	),
];

const withoutBucket = (
	selection: GroupedShowSelection,
	status: InclusionFilterStatus
): GroupedShowSelection => ({
	statuses: selection.statuses.filter((s) => s !== status),
	exclusionReasons: selection.exclusionReasons.filter(
		(encoded) => decodeExclusionReasonFilter(encoded)?.inclusionStatus !== status
	),
});

/** Selected params of a bucket: the encoded subset if any, else all known when fully selected. */
export const getSelectedReasons = (
	selection: GroupedShowSelection,
	status: InclusionFilterStatus,
	knownParams: string[]
): string[] => {
	const encoded = reasonsOf(selection, status);

	if (encoded.length > 0) {
		return encoded;
	}

	return selection.statuses.includes(status) ? [...knownParams] : [];
};

export const isBucketFullySelected = (
	selection: GroupedShowSelection,
	status: InclusionFilterStatus
): boolean => selection.statuses.includes(status) && reasonsOf(selection, status).length === 0;

/**
 * Bucket checkbox click: a fully selected bucket deselects entirely; anything else (off or
 * partial) becomes fully selected — matching the prototype's semantics.
 */
export const toggleBucket = (
	selection: GroupedShowSelection,
	status: InclusionFilterStatus
): GroupedShowSelection => {
	if (isBucketFullySelected(selection, status)) {
		// Enforce at least one selected bucket.
		if (selection.statuses.length === 1) {
			return selection;
		}

		return withoutBucket(selection, status);
	}

	const cleared = withoutBucket(selection, status);

	return { ...cleared, statuses: [...cleared.statuses, status] };
};

/** Reason checkbox click: toggles one sub-row, collapsing to a full bucket when all are on. */
export const toggleReason = (
	selection: GroupedShowSelection,
	status: InclusionFilterStatus,
	filterParam: string,
	knownParams: string[]
): GroupedShowSelection => {
	const current = getSelectedReasons(selection, status, knownParams);
	const next = current.includes(filterParam)
		? current.filter((param) => param !== filterParam)
		: [...current, filterParam];

	if (next.length === 0) {
		if (selection.statuses.length === 1 && selection.statuses.includes(status)) {
			return selection;
		}

		return withoutBucket(selection, status);
	}

	const cleared = withoutBucket(selection, status);
	const statuses = [...cleared.statuses, status];

	if (knownParams.every((param) => next.includes(param))) {
		return { ...cleared, statuses };
	}

	return {
		...cleared,
		statuses,
		exclusionReasons: [
			...cleared.exclusionReasons,
			// A child that stands for several reasons filters by all of them (PREC-4520).
			...next.flatMap((param) =>
				reasonParamsOf(param).map((member) => encodeExclusionReasonFilter(status, member))
			),
		],
	};
};
